import {Schema} from '@zaiusinc/app-forms-schema';
import {AppInstallation} from '../types/AppInstallation';
import {AppLogEntry} from '../types/AppLogEntry';
import {AppVersion} from '../types/AppVersion';
import {ZipApiError} from './ZipApiError';
import {ZipApiTimeoutError} from './ZipApiTimeoutError';
import {showToasts} from './toaster';
import {Intent} from '@blueprintjs/core';
import { CGBaseTypeDefinition, CGContentType, CGObjectDefinition, CGProductInstance, CGSourceMetadata, DataSyncItem, ObjectDefinition, OptimizelyProduct, ProductInstances } from '../types/DataSyncItem';
import { convertSchemaV2ToObjectDefinition } from './convertV2Schema';
import { DataSyncExecution } from '../types/DataSyncExecution';
import { CGLanguage } from '../types/ContentGraph';
import { Sort } from '../types/Sort';
import { GetCGDestinationSchemaResponse, GetSchemaResponse, GetSourceSchemaResponse, ListDestinationSchemasResponse, ListSourceSchemasResponse, Schema as SourceSchema } from '../types/Schema';
import { directoryStore } from '../stores/DirectoryStore';
import { OpalToolDescriptionsResponse, UpdateOpalToolDescriptionsRequest } from '../types/OpalToolDescriptions';

const ZIP_API_URI: string = process.env.REACT_APP_ZIP_API_URI || (() => {
  const url = new URL(window.location.href);
  const stem = url.host.split('.').slice(1);
  if (stem[stem.length - 2] === 'zaius') {
    // Legacy quirks.
    stem.unshift('directory');
  }
  url.host = ['api'].concat(stem).join('.');
  return url.origin;
})();

const AUTH_HEADERS: HeadersInit = process.env.REACT_APP_PRISM_ACCESS_TOKEN ? { 'authorization': `Bearer ${process.env.REACT_APP_PRISM_ACCESS_TOKEN}` } : {};

export type ToastIntent = 'info' | 'success' | 'warning' | 'danger';

export type EnvelopedResponse<K extends string, T> = {
  [key in K]: T;
};

export interface ListAppResponse {
  results: AppVersion[];
}

export interface ListAppInstallationsResponse {
  installations: AppInstallation[];
}

export interface FetchAppResponse {
  app: AppVersion;
}

export interface FetchAppVersionResponse {
  app_version: AppVersion;
}

export interface InstallResponse {
  app_installation: AppInstallation;
}

export interface FetchFormDataResponse {
  form_data_json: Schema.FormData;
}

export interface SubmitFormDataResponse {
  form_data_json: Schema.FormData;
  response_json: {
    redirect?: string;
    redirectMode?: 'url' | 'settings';
    errors: {[field: string]: string[]};
    toasts: Array<{intent: ToastIntent, message: string}>;
  };
}

export interface ListFunctionsResponse {
  function_endpoints: {
    [name: string]: string;
  };
}

export interface AuthorizationRequestResponse {
  form_data_json: Schema.FormData;
  response_json: {
    redirect?: string;
    redirectMode?: 'url' | 'settings';
    errors: {[field: string]: string[]};
    toasts: Array<{intent: ToastIntent, message: string}>;
  };
  auth_request_id: string;
}

export interface CanUninstallResponse {
  message?: string;
  uninstallable: boolean;
}

export interface ListAppLogEntriesResponse {
  log_entry: AppLogEntry[];
  cursor_token: string;
}

export interface DataSyncResponse {
  dataSync: DataSyncItem;
}

export interface ListDataSyncsResponse {
  dataSyncItems: DataSyncItem[];
}

export interface ListDataSyncExecutionResponse {
  executions: DataSyncExecution[];
}

export interface ListDestinationCGSchemasResponse {
  schemas: CGObjectDefinition[];
  preset: string;
}

export interface CreateCGSchemaRequest {
  content_type: CGContentType;
  preset: string;
  instance_id: string;
  tracker_id: string;
}

export interface SourceJob {
  name: string;
  description: string;
}

export interface ListSourcesResponse {
  name: string;
  jobs: SourceJob[];
}

export enum OpalToolStatus {
  REGISTERED = 'REGISTERED',
  NOT_REGISTERED = 'NOT_REGISTERED',
  REGISTERED_BY_ANOTHER = 'REGISTERED_BY_ANOTHER'
}

export interface OpalTool {
  id: string;
  name: string;
  description: string;
  status: OpalToolStatus;
  discovery_url: string;
}

export interface ListOpalToolResponse {
  instance_id: string;
  tools: OpalTool[];
}

export interface RegisterOpalToolResponse {
  tool: OpalTool;
}

export interface OpalInstance {
  InstanceId: string;
  InstanceName: string;
  ProductName: string;
  OrganizationId: string;
}

export interface ListOpalInstancesResponse {
  totalItemCount: number;
  items: OpalInstance[];
}

export abstract class ZipApi {

  public static async listApps(trackerId?: string): Promise<ListAppResponse> {
    const queryParams = trackerId === undefined ? '' : `?tracker_id=${trackerId}`;
    const response = await this.performGet(`${ZIP_API_URI}/v1/apps${queryParams}`);
    return await response.json();
  }

  public static async listAppInstallations(trackerId: string): Promise<ListAppInstallationsResponse> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/accounts/${trackerId}/installs`);
    return await response.json();
  }

  public static async listAppInstalls(trackerId: string, appFeature: string): Promise<ListAppInstallationsResponse> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/accounts/${trackerId}/installs?includes_features=${appFeature}`);
    return await response.json();
  }

  public static async fetchApp(appId: string): Promise<FetchAppResponse> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/apps/${appId}`);
    return await response.json();
  }

  public static async fetchAppVersion(appId: string, version: string): Promise<FetchAppVersionResponse> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/apps/${appId}/versions/${version}`);
    return await response.json();
  }

  public static async install(trackerId: string, appId: string, version?: string): Promise<InstallResponse> {
    const response = await this.performPost(
      `${ZIP_API_URI}/v1/accounts/${trackerId}/installs`,
      JSON.stringify({app_id: appId, version})
    );
    return await response.json();
  }

  public static async canUninstall(installId: number): Promise<CanUninstallResponse> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/installs/${installId}/canUninstall`);
    return await response.json();
  }

  public static async uninstall(trackerId: string, appId: string): Promise<InstallResponse> {
    const response = await this.performRequest(
      'DELETE',
      `${ZIP_API_URI}/v1/accounts/${trackerId}/installs/${appId}`
    );
    return await response.json();
  }

  public static async createDataSync(dataSync: DataSyncItem): Promise<DataSyncResponse> {
    // use nestes_mappings for new data syncs
    const response = await this.performPost(`${ZIP_API_URI}/v1/data-syncs`, JSON.stringify(dataSync));
    const result = await response.json();
    if (result.dataSync) {
      result.dataSync = new DataSyncItem(result.dataSync);
    }
    return result;
  }

  public static async updateDataSync(dataSync: DataSyncItem): Promise<DataSyncResponse> {
    const response = await this.performRequest(
      'PUT',
      `${ZIP_API_URI}/v1/data-syncs/${dataSync.id}`,
      JSON.stringify(dataSync)
    );
    const result = await response.json();
    if (result.dataSync) {
      result.dataSync = new DataSyncItem(result.dataSync);
    }
    return result;
  }

  public static async getDataSync(dataSyncId: string): Promise<DataSyncResponse> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/data-syncs/${dataSyncId}`);
    const result = await response.json();
    if (result.dataSync) {
      result.dataSync = new DataSyncItem(result.dataSync);
    }
    return result;
  }

  public static async listDataSyncs(
    offset: number,
    limit: number,
    sort?: Sort,
    trackerId?: string
  ): Promise<ListDataSyncsResponse> {
    const params = new URLSearchParams({
      'offset': offset.toString(),
      'limit': limit.toString(),
    });

    if (sort) {
      params.append('sort.field', sort.field);
      params.append('sort.direction', sort.direction.toString());
    }

    if (trackerId) {
      params.append('tracker_id', trackerId);
    }

    const queryString = params.toString();
    const response = await this.performGet(`${ZIP_API_URI}/v1/data-syncs?${queryString}`);
    return await response.json();
  }

  public static async enableDataSync(dataSyncId: string) {
    const response = await this.performPatch(`${ZIP_API_URI}/v1/data-syncs/${dataSyncId}/enable`);
    return response.ok;
  }

  public static async disableDataSync(dataSyncId: string) {
    const response = await this.performPatch(`${ZIP_API_URI}/v1/data-syncs/${dataSyncId}/disable`);
    return response.ok;
  }

  public static async deleteDataSync(dataSyncId: string) {
    const response = await this.performRequest('DELETE', `${ZIP_API_URI}/v1/data-syncs/${dataSyncId}`);
    return response.ok;
  }

  public static async listDataSyncExecutions(dataSyncId: string, offset: number, limit: number):
    Promise<ListDataSyncExecutionResponse> {
    const params = new URLSearchParams({
      'offset': offset.toString(),
      'limit': limit.toString(),
      'sort.field': 'createdAt',
      'sort.direction': '1',
    });

    const queryString = params.toString();
    const response = await this.performGet(`${ZIP_API_URI}/v1/data-syncs/${dataSyncId}/executions?${queryString}`);
    return await response.json();
  }

  public static async listProductInstances(trackerId: string, products?: OptimizelyProduct):
  Promise<ProductInstances[]> {
    const queryParams = products ? `?products=${products}` : '';
    const response = await this.performGet(`${ZIP_API_URI}/v1/accounts/${trackerId}/product_instances${queryParams}`);
    const body =  await response.json() as EnvelopedResponse<'instances', ProductInstances[]>;
    if (body.instances) {
      return body.instances;
    } else {
      return [];
    }
  }

  public static async listOpalInstances(organizationId: string, userEmail: string): Promise<OpalInstance[]> {
    const url = `${process.env.REACT_APP_USER_MANAGEMENT_URI}/api/users/${userEmail}/instancepermissions?size=1000`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${directoryStore.getOptiAccessToken()}`
    };

    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new ZipApiError(response);
    }

    const body = await response.json() as ListOpalInstancesResponse;

    if (body.items) {
      return body.items.filter((instance) => instance.OrganizationId === organizationId && instance.ProductName === 'Opal');
    } else {
      return [];
    }
  }

  public static async listOpalTools(
    trackerId: string,
    instanceId: string,
    installationId: number,
    optiidToken: string
  ): Promise<ListOpalToolResponse> {
    const params = new URLSearchParams({
      'installation_id': installationId.toString()
    });

    const queryString = params.toString();
    const headers = {
      'X-OptiID-Token': optiidToken
    };

    const url = `${ZIP_API_URI}/v1/accounts/${trackerId}/opal/${instanceId}/tools?${queryString}`;
    const response = await this.performGet(url, headers);
    const jsonResponse = await response.json() as ListOpalToolResponse;
    if (!jsonResponse.tools) {
      jsonResponse.tools = [];
    }

    return jsonResponse;
  }

  public static async registerOpalTool(
    trackerId: string,
    instanceId: string,
    toolName: string,
    installationId: number,
    optiidToken: string
  ): Promise<RegisterOpalToolResponse> {
    const requestBody = {
      tracker_id: trackerId,
      instance_id: instanceId,
      tool_name: toolName,
      installation_id: installationId
    };

    const headers = {
      'X-OptiID-Token': optiidToken
    };

    const response = await this.performPost(
      `${ZIP_API_URI}/v1/accounts/${trackerId}/opal/${instanceId}/tools`,
      JSON.stringify(requestBody),
      headers
    );
    return await response.json() as RegisterOpalToolResponse;
  }

  public static async unregisterOpalTool(
    trackerId: string,
    instanceId: string,
    toolId: string,
    optiidToken: string
  ): Promise<void> {
    const headers = {
      'X-OptiID-Token': optiidToken
    };

    const response = await this.performRequest(
      'DELETE',
      `${ZIP_API_URI}/v1/accounts/${trackerId}/opal/${instanceId}/tools/${toolId}`,
      undefined,
      headers
    );
    return await response.json();
  }

  public static async syncOpalTool(
    trackerId: string,
    instanceId: string,
    toolId: string,
    optiidToken: string
  ): Promise<void> {
    const headers = {
      'X-OptiID-Token': optiidToken
    };

    await this.performPost(
      `${ZIP_API_URI}/v1/accounts/${trackerId}/opal/${instanceId}/tools/${toolId}/sync`,
      JSON.stringify({}),
      headers
    );
  }

  public static async getOpalToolDescriptions(
    trackerId: string,
    appId: string,
    functionName: string
  ): Promise<OpalToolDescriptionsResponse> {
    const response = await this.performGet(
      `${ZIP_API_URI}/v1/accounts/${trackerId}/app/${appId}/function/${functionName}/opal-tools-descriptions`
    );
    return await response.json() as OpalToolDescriptionsResponse;
  }

  public static async updateOpalToolDescriptions(
    trackerId: string,
    appId: string,
    functionName: string,
    descriptions: UpdateOpalToolDescriptionsRequest,
    optiidToken: string
  ): Promise<void> {
    const headers = {
      'X-OptiID-Token': optiidToken
    };
    await this.performRequest(
      'PATCH',
      `${ZIP_API_URI}/v1/accounts/${trackerId}/app/${appId}/function/${functionName}/opal-tools-descriptions`,
      JSON.stringify(descriptions),
      headers
    );
  }

  public static async deleteOpalToolDescriptions(
    trackerId: string,
    appId: string,
    functionName: string,
    optiidToken: string
  ): Promise<void> {
    const headers = {
      'X-OptiID-Token': optiidToken
    };
    await this.performRequest(
      'DELETE',
      `${ZIP_API_URI}/v1/accounts/${trackerId}/app/${appId}/function/${functionName}/opal-tools-descriptions`,
      undefined,
      headers
    );
  }

  public static async listODPSchemas(trackerId: string): Promise<ObjectDefinition[]> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/odp/schemas?tracker_id=${trackerId}`);
    const body =  await response.json() as EnvelopedResponse<'schemas', ObjectDefinition[]>;
    if (body.schemas) {
      return body.schemas;
    } else {
      return [];
    }
  }

  public static async getCGSourceMetadata(trackerId: string, instanceId: string): Promise<CGSourceMetadata> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/cg/source-metadata?tracker_id=${trackerId}&instance_id=${instanceId}`);
    return await response.json();
  }

  public static async updateCGSourceMetadata(
    trackerId: string,
    instanceId: string,
    languages: [CGLanguage]
  ): Promise<string> {
    const response = await this.performPatch(
      `${ZIP_API_URI}/v1/cg/source-metadata`,
      JSON.stringify({'trackerId': trackerId, 'instanceId': instanceId, 'additional_languages': languages})
    );
    return await response.json();
  }

  public static async listCGBaseTypes(trackerId: string, instanceId: string): Promise<CGBaseTypeDefinition[]> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/cg/list-base-contracts?tracker_id=${trackerId}&instance_id=${instanceId}`);
    const body =  await response.json() as EnvelopedResponse<'base_contracts', CGBaseTypeDefinition[]>;
    if (body.base_contracts) {
      return body.base_contracts;
    } else {
      return [];
    }
  }

  public static async listSourceCGSchemas(trackerId: string, instanceId: string): Promise<ObjectDefinition[]> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/cg/source/schemas?tracker_id=${trackerId}&instance_id=${instanceId}`);
    const body =  await response.json() as EnvelopedResponse<'schemas', ObjectDefinition[]>;
    if (body.schemas) {
      return body.schemas;
    } else {
      return [];
    }
  }

  public static async listDestinationCGSchemas(
    trackerId: string,
    instanceId: string
  ): Promise<ListDestinationCGSchemasResponse> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/cg/destination/schemas?tracker_id=${trackerId}&instance_id=${instanceId}`);
    return await response.json() as ListDestinationCGSchemasResponse;
  }

  public static async listDestinationSchemas(
    trackerId: string,
    product: string,
    instanceId: string): Promise<ListDestinationSchemasResponse> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/accounts/${trackerId}/${product}/${instanceId}/destinations/schemas`);
    return await response.json() as ListDestinationSchemasResponse;
  }

  public static async listSourceSchemas(
    trackerId: string,
    product: string,
    instanceId: string
  ): Promise<ListSourceSchemasResponse> {
    const response = await this.performGet(
      `${ZIP_API_URI}/v1/accounts/${trackerId}/${product}/${instanceId}/sources/schemas`
    );
    return await response.json() as ListSourceSchemasResponse;
  }

  public static async getSourceSchema(
    trackerId: string,
    product: string,
    instanceId: string,
    schemaId: string
  ): Promise<SourceSchema> {
    const response = await this.performGet(
      `${ZIP_API_URI}/v1/accounts/${trackerId}/${product}/${instanceId}/sources/schemas/${schemaId}`
    );
    const body = await response.json() as GetSourceSchemaResponse;
    return body.schema;
  }

  public static async getDestinationSchema(
    trackerId: string,
    product: string,
    instanceId: string,
    schemaId: string
  ) {
    const response = await this.performGet(`${ZIP_API_URI}/v1/accounts/${trackerId}/${product}/${instanceId}/destinations/schemas/${schemaId}`);
    const body = await response.json() as GetCGDestinationSchemaResponse;
    return body.schema;
  }

  public static async saveCGSchema(createCGSchemaRequest: CreateCGSchemaRequest): Promise<void> {
    return await this.performPost(`${ZIP_API_URI}/v1/cg/schema`, JSON.stringify(createCGSchemaRequest));
  }

  public static async getCGInstance(trackerId: string, instanceId: string): Promise<CGProductInstance> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/cg/instance?tracker_id=${trackerId}&instance_id=${instanceId}`);
    return await response.json() as CGProductInstance;
  }

  public static async checkCGTypeExists(
    trackerId: string,
    instanceId: string,
    typeName: string
  ): Promise<{exists: boolean; message: string}> {
    const response = await this.performGet(
      `${ZIP_API_URI}/v1/cg/instances/${instanceId}/types/${encodeURIComponent(typeName)}/exists?tracker_id=${trackerId}`
    );
    return await response.json();
  }

  public static async listAppSources(trackerId: string, appId: string): Promise<ListSourcesResponse[]> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/accounts/${trackerId}/apps/${appId}/sources`);
    const body =  await response.json() as EnvelopedResponse<'sources', ListSourcesResponse[]>;
    if (body.sources) {
      return body.sources;
    } else {
      return [];
    }
  }

  public static async listAppDestinations(trackerId: string, appId: string): Promise<string[]> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/accounts/${trackerId}/apps/${appId}/destinations`);
    const body =  await response.json() as EnvelopedResponse<'destinations', string[]>;
    if (body.destinations) {
      return body.destinations;
    } else {
      return [];
    }
  }

  public static async listHubAppSourceSchema(trackerId: string,
                                             appId: string,
                                             source: string): Promise<ObjectDefinition> {
    const response = await this.performGet(`${ZIP_API_URI}/v2/accounts/${trackerId}/apps/${appId}/sources/${source}/schema`);
    const body = await response.json() as GetSchemaResponse;

    // Convert v2 format to ObjectDefinition format with nested field support
    return convertSchemaV2ToObjectDefinition(body.schema);
  }

  public static async listHubAppDestinationSchema(trackerId: string,
                                                  appId: string,
                                                  destination: string): Promise<ObjectDefinition> {
    const response = await this.performGet(`${ZIP_API_URI}/v2/accounts/${trackerId}/apps/${appId}/destinations/${destination}/schema`);
    const body = await response.json() as GetSchemaResponse;

    // Convert v2 format to ObjectDefinition format with nested field support
    return convertSchemaV2ToObjectDefinition(body.schema);
  }

  public static async fetchFormData(installId: number): Promise<FetchFormDataResponse> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/installs/${installId}/settings`);
    const data = await response.json();
    return {
      form_data_json: JSON.parse(data.form_data_json)
    };
  }

  public static async submitFormData(installId: number, jsonPayload: string): Promise<SubmitFormDataResponse> {
    const response = await this.performPost(`${ZIP_API_URI}/v1/installs/${installId}/settings`, jsonPayload);
    const data = await response.json();
    return {
      form_data_json: JSON.parse(data.form_data_json),
      response_json: JSON.parse(data.response_json)
    };
  }

  public static async listFunctions(installId: number): Promise<ListFunctionsResponse['function_endpoints']> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/installs/${installId}/functions`);
    return (await response.json() as ListFunctionsResponse).function_endpoints;
  }

  public static async listAppLogEntries(
    jsonPayload: string,
    attemptsLeft: number = 3
  ): Promise<ListAppLogEntriesResponse> {
    try {
      const response = await this.performPost(`${ZIP_API_URI}/v1/logging/search`, jsonPayload);
      return await response.json();
    } catch (e) {
      if (e instanceof ZipApiError && attemptsLeft > 0) {
        await new Promise( (resolve) => setTimeout(resolve, 100));
        return this.listAppLogEntries(jsonPayload, attemptsLeft - 1);
      }
      if (e instanceof ZipApiTimeoutError) {
        showToasts([
          {
            intent: Intent.DANGER,
            message: 'Timeout while fetching logs. Please try again. Consider narrowing down the date range.'
          }
          ]);
        return {
            log_entry: [],
            cursor_token: ''
        };
      }

      throw e;
    }
  }

  public static async authorizationRequest(installId: number, jsonPayload: string):
    Promise<AuthorizationRequestResponse> {
    const response = await this.performPost(
      `${ZIP_API_URI}/v1/installs/${installId}/authorization/request`, jsonPayload
    );
    const data = await response.json();
    return {
      form_data_json: JSON.parse(data.form_data_json),
      response_json: JSON.parse(data.response_json),
      auth_request_id: data.auth_request_id
    };
  }

  public static async triggerSync(dataSyncId: string, jobName?: string): Promise<boolean> {
    const requestBody: any = {};
    if (jobName) {
        requestBody.job_name = jobName;
    }
    const jsonRequestBody = JSON.stringify(requestBody);
    const response = await this.performPatch(`${ZIP_API_URI}/v1/data-syncs/${dataSyncId}/trigger`, jsonRequestBody);
    return response.ok;
  }

  private static async performGet(url: string, headers?: Record<string, string>) {
    const response = await fetch(url, {
      credentials: 'include',
      headers: {
        ...headers,
        ...AUTH_HEADERS
      }
    });
    if (!response.ok) {
      throw new ZipApiError(response);
    }
    return response;
  }

  private static async performPost(url: string, body: string, headers?: Record<string, string>) {
    return this.performRequest('POST', url, body, headers);
  }

  private static async performPatch(url: string, body?: string, headers?: Record<string, string>) {
    return this.performRequest('PATCH', url, body, headers);
  }

  private static async performRequest(method: string, url: string, body?: string, headers?: Record<string, string>) {
    let response;
    try {
      response = await fetch(url, {
        method,
        body,
        headers: {
          ...headers,
          'Content-Type': 'application/json',
          ...AUTH_HEADERS,
        },
        credentials: 'include'
      });
    } catch (e) {
      if (e instanceof TypeError) {
        throw new ZipApiTimeoutError();
      }

      throw e;
    }

    if (!response.ok) {
      throw new ZipApiError(response);
    }
    return response;
  }
}
